import React, { useCallback, useEffect, useState } from 'react'
import { Button, Card, Col, Empty, Image, Modal, Pagination, Row, Spin, message } from 'antd'
import moment from 'moment'
import { getTenantDirectMyList } from '../../utils/api'
import './tenantDirectModal.scss'

const PAGE_SIZE = 5

const extractList = (payload, depth = 0) => {
    if (payload == null || depth > 3) return []
    if (Array.isArray(payload)) return payload
    if (typeof payload !== 'object') return []

    const listKeys = ['pageContent', 'list', 'records', 'rows', 'content', 'dataList', 'items', 'data']
    for (let i = 0; i < listKeys.length; i += 1) {
        const val = payload[listKeys[i]]
        if (Array.isArray(val)) return val
        if (val && typeof val === 'object') {
            const nested = extractList(val, depth + 1)
            if (nested.length) return nested
        }
    }
    return []
}

const parseTotal = (payload, listLength) => {
    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
        return listLength
    }
    const total = payload.total ?? payload.totalCount ?? payload.count ?? payload.totalElements ?? payload.totalSize
    if (total != null && !Number.isNaN(Number(total))) {
        return Number(total)
    }
    return listLength
}

const getStatusMeta = (status) => {
    if (String(status) === '1') {
        return { text: '已解决', className: 'is-resolved' }
    }
    return { text: '待解决', className: 'is-pending' }
}

const parseImageList = (image) => {
    if (!image) return []
    if (Array.isArray(image)) return image.filter(Boolean)
    return String(image).split(',').map((url) => url.trim()).filter(Boolean)
}

const TenantDirectMyList = () => {
    const [list, setList] = useState([])
    const [currentPage, setCurrentPage] = useState(1)
    const [total, setTotal] = useState(0)
    const [loading, setLoading] = useState(false)
    const [detailVisible, setDetailVisible] = useState(false)
    const [detailItem, setDetailItem] = useState(null)

    const fetchList = useCallback(async (page = 1) => {
        setLoading(true)
        try {
            const res = await getTenantDirectMyList({
                current: page,
                pageIndex: page - 1,
                pageNum: page,
                pageSize: PAGE_SIZE,
            })
            if (!res || Number(res.code) !== 0) {
                message.error((res && res.message) || '获取我的租情直通车失败')
                setList([])
                setTotal(0)
                return
            }
            const pageList = extractList(res.data)
            setList(pageList)
            setCurrentPage(page)
            setTotal(parseTotal(res.data, pageList.length))
        } catch (e) {
            message.error('获取我的租情直通车失败')
            setList([])
            setTotal(0)
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        fetchList(1)
    }, [fetchList])

    const handlePageChange = (page) => {
        fetchList(page)
    }

    const openDetail = (item) => {
        setDetailItem(item)
        setDetailVisible(true)
    }

    const closeDetail = () => {
        setDetailVisible(false)
        setDetailItem(null)
    }

    const renderDetailModal = () => {
        if (!detailItem) return null
        const statusMeta = getStatusMeta(detailItem.status)
        const imageList = parseImageList(detailItem.image)

        return (
            <Modal
                title="详情"
                visible={detailVisible}
                onCancel={closeDetail}
                footer={null}
                width={640}
                destroyOnClose
                className="tenant-direct-my-detail-modal"
            >
                <div className="tenant-direct-my-detail">
                    <div className="tenant-direct-my-detail__header">
                        <span className={`tenant-direct-my-list__status ${statusMeta.className}`}>
                            {statusMeta.text}
                        </span>
                        <span className="tenant-direct-my-detail__time">
                            {detailItem.createTime ? moment(detailItem.createTime).format('YYYY-MM-DD HH:mm') : '暂无提交时间'}
                        </span>
                    </div>
                    <div className="tenant-direct-my-detail__section">
                        <div className="tenant-direct-my-detail__label">问题和意见</div>
                        <div className="tenant-direct-my-detail__value">
                            {detailItem.description || '暂无问题描述'}
                        </div>
                    </div>
                    <div className="tenant-direct-my-detail__section">
                        <div className="tenant-direct-my-detail__label">图片</div>
                        <div className="tenant-direct-my-detail__value">
                            {imageList.length ? (
                                <Image.PreviewGroup>
                                    <div className="tenant-direct-my-detail__images">
                                        {imageList.map((url) => (
                                            <Image
                                                key={url}
                                                src={url}
                                                alt=""
                                                width={96}
                                                height={96}
                                                style={{ objectFit: 'cover', borderRadius: 6 }}
                                            />
                                        ))}
                                    </div>
                                </Image.PreviewGroup>
                            ) : '暂无图片'}
                        </div>
                    </div>
                    <div className="tenant-direct-my-detail__section">
                        <div className="tenant-direct-my-detail__label">联系电话</div>
                        <div className="tenant-direct-my-detail__value">
                            {detailItem.contactTel || '暂无联系电话'}
                        </div>
                    </div>
                    <div className="tenant-direct-my-detail__section">
                        <div className="tenant-direct-my-detail__label">提交日期</div>
                        <div className="tenant-direct-my-detail__value">
                            {detailItem.createTime ? moment(detailItem.createTime).format('YYYY-MM-DD') : '暂无提交日期'}
                        </div>
                    </div>
                    {String(detailItem.status) === '0' ? (
                        <div className="tenant-direct-my-detail__tip">
                            我们会尽快处理问题与您联系，请耐心等待
                        </div>
                    ) : null}
                    {detailItem.resolveFeedback ? (
                        <div className="tenant-direct-my-detail__section">
                            <div className="tenant-direct-my-detail__label">处理反馈</div>
                            <div className="tenant-direct-my-detail__value">
                                {detailItem.resolveFeedback}
                            </div>
                        </div>
                    ) : null}
                    {detailItem.resolveDate ? (
                        <div className="tenant-direct-my-detail__section">
                            <div className="tenant-direct-my-detail__label">解决日期</div>
                            <div className="tenant-direct-my-detail__value">
                                {moment(detailItem.resolveDate).format('YYYY-MM-DD')}
                            </div>
                        </div>
                    ) : null}
                </div>
            </Modal>
        )
    }

    if (loading && !list.length) {
        return (
            <div className="tenant-direct-my-list__loading">
                <Spin />
            </div>
        )
    }

    if (!list.length) {
        return <Empty style={{ height: '545px' }} description="暂无提问记录" />
    }

    return (
        <div className="tenant-direct-my-list">
            <Row gutter={[0, 24]}>
                {list.map((item, index) => {
                    const statusMeta = getStatusMeta(item.status)
                    const key = item.id != null ? item.id : index
                    return (
                        <Col span={24} key={key}>
                            <Card
                                bordered
                                className="tenant-direct-my-list__card"
                                style={{ border: '1px solid #A0B5FB' }}
                                headStyle={{
                                    borderBottom: '1px solid #A0B5FB',
                                    padding: '0 24px',
                                    background: '#F3F6FF',
                                    color: '#999999',
                                    fontSize: '16px',
                                }}
                                title={(
                                    <div className="tenant-direct-my-list__title">
                                        <span className={`tenant-direct-my-list__status ${statusMeta.className}`}>
                                            {statusMeta.text}
                                        </span>
                                        <span className="tenant-direct-my-list__time">
                                            {item.createTime ? moment(item.createTime).format('YYYY-MM-DD HH:mm') : '暂无提交时间'}
                                        </span>
                                    </div>
                                )}
                            >
                                <Row align="middle" gutter={16}>
                                    <Col xs={18} sm={18} md={20} lg={20} xl={20}>
                                        <div className="tenant-direct-my-list__desc">
                                            {item.description || '暂无问题描述'}
                                        </div>
                                    </Col>
                                    <Col xs={6} sm={6} md={4} lg={4} xl={4} style={{ textAlign: 'right' }}>
                                        <Button
                                            type="link"
                                            className="tenant-direct-my-list__detail-btn"
                                            onClick={() => openDetail(item)}
                                        >
                                            详情
                                        </Button>
                                    </Col>
                                </Row>
                            </Card>
                        </Col>
                    )
                })}
            </Row>
            {total > PAGE_SIZE ? (
                <div className="tenant-direct-my-list__pagination">
                    <Pagination
                        current={currentPage}
                        pageSize={PAGE_SIZE}
                        total={total}
                        showSizeChanger={false}
                        onChange={handlePageChange}
                    />
                </div>
            ) : null}
            {renderDetailModal()}
        </div>
    )
}

export default TenantDirectMyList
